import { GLMappingRepository } from "../../domain/repositories/GLMappingRepository";
import {
  AccountTreeResultEntity,
  DocumentGlType,
  JournalPickerType,
} from "../../domain/entities/AccountTreeEntity";
import { fetchAccountTree, FetchAccountTreeOptions } from "../api/glMappingsApi";
import { AccountTreeMapper } from "../mappers/AccountTreeMapper";

export class GLMappingRepositoryImpl implements GLMappingRepository {
  async fetchAccountTreeSummary(
    docType: DocumentGlType,
    selectedAccountId?: string,
    journalType?: JournalPickerType,
  ): Promise<AccountTreeResultEntity> {
    return this.fetchAccountTree({ docType, selectedAccountId, journalType });
  }

  async fetchAccountTreeForType(
    docType: DocumentGlType,
    accountType: string,
    selectedAccountId?: string,
    journalType?: JournalPickerType,
  ): Promise<AccountTreeResultEntity> {
    return this.fetchAccountTree({
      docType,
      accountType,
      selectedAccountId,
      journalType,
    });
  }

  async fetchAccountTreeSearch(
    docType: DocumentGlType,
    search: string,
    limit?: number,
    offset?: number,
    journalType?: JournalPickerType,
  ): Promise<AccountTreeResultEntity> {
    return this.fetchAccountTree({ docType, search, limit, offset, journalType });
  }

  /** Load all selectable leaves by fetching each account_type tree (legacy flat list consumers). */
  async fetchAllSelectableLeaves(
    docType: DocumentGlType,
    journalType?: JournalPickerType,
  ): Promise<AccountTreeResultEntity> {
    const summary = await this.fetchAccountTreeSummary(
      docType,
      undefined,
      journalType,
    );
    if (!summary.success) {
      return summary;
    }

    const groups = [...summary.groups];
    for (const group of summary.groups) {
      if (group.total_leaves <= 0) continue;
      if (group.nodes.length > 0) continue;
      const typeResult = await this.fetchAccountTreeForType(
        docType,
        group.account_type,
        undefined,
        journalType,
      );
      const typeGroup = typeResult.groups.find(
        (g) => g.account_type === group.account_type,
      );
      if (typeGroup) {
        const idx = groups.findIndex((g) => g.account_type === group.account_type);
        if (idx >= 0) {
          groups[idx] = typeGroup;
        }
      }
    }

    return {
      success: true,
      groups,
      pagination: null,
      selectedAccountType: null,
    };
  }

  private async fetchAccountTree(
    options: FetchAccountTreeOptions,
  ): Promise<AccountTreeResultEntity> {
    try {
      const response = await fetchAccountTree(options);
      return AccountTreeMapper.toResultEntity(response);
    } catch (error: unknown) {
      console.error("Error in GLMappingRepository:", error);
      throw error;
    }
  }
}

export const glMappingRepository = new GLMappingRepositoryImpl();
